import { ref, watch } from "@vue/composition-api";
import { Call, CallCreateResponse, CallStatus, CallType } from "@scrile/api-provider/dist/api/CallsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { WebRTCConnection } from "@scrile/api-provider/dist/api/WebRTCProvider";
import { Appointment } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentProvider";
import { StreamingHelper } from "@scrile/streaming-client";
import useAuthUser from "@/hooks/useAuthUser";
import useDoNotDisturb from "@/hooks/useDoNotDisturb";
import providers from "@/lib/api";
import AudioPlayer from "@/lib/audioPlayer";
import { isMobile } from "@/lib/browserUtils";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import CallsLogger, { LoggerContextWithMessage } from "@/modules/callsLogger";
import { PreventLogout } from "@/types";
import { connectionStateEnum, EVENTS, PreventLogoutType } from "@/const";
import { pauseGlobalPlayer } from "@/components/GlobalAudioPlayer/helpers";

const ERROR_DISPLAY_TIME = 6000;
const DEFAULT_STATS_RECORDS = 20;
// getUserMedia refusals: the user or the browser settings denied the access
const PERMISSION_ERRORS = ["NotAllowedError", "SecurityError", "PermissionDeniedError"];

export const isPermissionError = (error: unknown) =>
  PERMISSION_ERRORS.includes((error as Error | undefined)?.name ?? "");

type ProducersUpdatedData = {
  video: boolean;
  audio: boolean;
  screen: boolean;
};

// producers-updated, or app-data from the opponent (data channel): a video chat request or its end ({ video }),
// or the state of their camera during the video chat ({ cameraOff })
type OpponentData = Pick<ProducersUpdatedData, "video"> | { cameraOff: boolean };

export default function useCalls() {
  const { authUserStrict } = useAuthUser();
  const { doNotDisturb, setDoNotDisturb } = useDoNotDisturb();

  let WebRTC: StreamingHelper;
  const call = ref<Call | null>(null);
  const callConnection = ref<WebRTCConnection | null>(null);
  const opponentUser = ref<UserPublicData | null>(null);
  const showNotification = ref(false);
  const showConnectionError = ref(false);
  const connectionErrorMessage = ref("");
  const connectionErrorTitle = ref("");
  let errorTimeout: number | NodeJS.Timeout = 0;
  const opponentSubscriptionStatus = ref<"follower" | "subscriber" | "user">("user");
  const opponentStream = ref<MediaStream>(new MediaStream([]));
  const localStream = ref<MediaStream>(new MediaStream([]));
  const localReadyVideoState = ref(false);
  const opponentReadyVideoState = ref(false);
  const localVideoState = ref(false);
  const opponentVideoState = ref(false);
  const loading = ref(false);
  const connectingState = ref<connectionStateEnum>(connectionStateEnum.EMPTY);
  const isAccepted = ref(false);
  const cameraOff = ref(false);
  const opponentCameraOff = ref(false);

  const getWebRTCLoggingContext = () => {
    return {
      consumer: {
        roomId: (WebRTC.webRTCData?.consumer.client as any)?.roomId,
        userId: (WebRTC.webRTCData?.consumer.client as any)?.userId,
      },
      producer: {
        roomId: (WebRTC.webRTCData?.producer.client as any)?.roomId,
        userId: (WebRTC.webRTCData?.producer.client as any)?.userId,
      },
      stats: WebRTC.getStatisticHistory(DEFAULT_STATS_RECORDS),
      info: {
        callId: call.value?.id,
        time: new Date().getTime(),
      },
    };
  };

  const collectErrorLogs = (eventName: string, error: Error, context?: Record<string, any>) =>
    CallsLogger.collectLogs(`[CAll CONTROLLER]:ERROR - ${eventName}`, {
      ...getWebRTCLoggingContext(),
      ...context,
      streaming: true,
      error,
    });

  const businessLogicLog = (logMessage: string, context?: Record<string, any>) =>
    CallsLogger.collectLogs(`[CAll CONTROLLER] - ${logMessage}`, {
      call: call.value,
      connection: callConnection.value,
      ...getWebRTCLoggingContext(),
      ...context,
      streaming: true,
    });

  const onLocalStreamChanged = () => {
    localVideoState.value = localStream.value.getVideoTracks().length > 0;
  };

  const onOpponentStreamChanged = () => {
    opponentVideoState.value = opponentStream.value.getVideoTracks().length > 0;
  };

  // Camera off in the video chat is track.enabled = false: the video track stays in the call and sends black frames,
  // the opponent keeps sending their video and gets { cameraOff } to show the avatar instead of the black picture
  const applyCameraState = (stream: MediaStream) => {
    stream.getVideoTracks().forEach(track => (track.enabled = !cameraOff.value));
    return stream;
  };

  const resetCameraState = () => {
    cameraOff.value = false;
    opponentCameraOff.value = false;
  };

  const closeTracks = () => {
    opponentStream.value.getTracks().forEach(track => track.stop());
    localStream.value.getTracks().forEach(track => track.stop());
    opponentStream.value = new MediaStream();
    localStream.value = new MediaStream();
    onOpponentStreamChanged();
    onLocalStreamChanged();
  };

  const resetLocalState = (immediate: boolean = false) => {
    opponentReadyVideoState.value = false;
    localReadyVideoState.value = false;
    callConnection.value = null;
    showConnectionError.value = false;
    connectionErrorMessage.value = "";
    connectionErrorTitle.value = "";
    isAccepted.value = false;
    resetCameraState();
    if (errorTimeout !== 0) {
      clearTimeout(errorTimeout as number);
      errorTimeout = 0;
    }
    const clear = () => {
      call.value = null;
      opponentUser.value = null;
      showNotification.value = false;
      connectingState.value = connectionStateEnum.EMPTY;
    };
    if (immediate) clear();
    else setTimeout(() => clear(), 2000);
  };

  const callEnd = (callId: string) => {
    setDoNotDisturb(false);
    providers.CallsProvider.setReceiveErrors(false);
    businessLogicLog("Call end");
    emitter.emit(EVENTS.PREVENT_LOGOUT, { value: false, type: PreventLogoutType.CALL_CONTROLLER });
    return providers.CallsProvider.end({ callId });
  };

  const reset = async (immediate: boolean = false, ignore: boolean = false) => {
    if (!ignore && call.value) call.value = await callEnd(call.value.id);
    closeTracks();
    WebRTC.closeAllStreams();
    resetLocalState(immediate);
  };

  const connectionErrorCb = async () => {
    showConnectionError.value = true;
    errorTimeout = setTimeout(() => resetLocalState(), ERROR_DISPLAY_TIME);
    closeTracks();
    WebRTC.closeAllStreams();
    if (call.value) call.value = await callEnd(call.value.id);
  };

  const getStream = async (video: MediaStreamConstraints["video"], audio: MediaStreamConstraints["audio"]) => {
    try {
      return await WebRTC.getStream({ video, audio });
    } catch (error) {
      collectErrorLogs("Getting stream", error as Error);
      if (video) {
        connectionErrorMessage.value = $t("Please allow the site access to the camera in your Browser settings menu.");
      } else if (isPermissionError(error)) {
        // calls start with the microphone only: the old text asked for the camera and spoke of a lost connection
        connectionErrorTitle.value = $t("No access to the microphone");
        connectionErrorMessage.value = $t(
          "Please allow the site access to the microphone: reload the page, call again and tap Allow. If the browser does not ask, open the site settings (on iPhone: Website Settings in the Safari address bar menu) and set Microphone to Allow."
        );
      } else {
        connectionErrorTitle.value = $t("Microphone is not available");
        connectionErrorMessage.value = $t(
          "Could not turn on the microphone. Check that it is not used by another app and call again."
        );
      }
      throw error;
    }
  };

  const getDevicePermission = async ({ video, audio }: MediaStreamConstraints) => {
    (await getStream(video, audio)).getTracks().forEach(track => track.stop());
  };

  // The microphone already published in the call stays the same track: entering and leaving the video chat takes only
  // the camera from the device. A new audio track would close the old one, and between closing the old sound and consuming
  // the new video the opponent would have no stream at all: streaming-client takes it as a lost call and ends it. While
  // the own camera is off there are no frames, and this gap is long (the call from the iPhone side dropped this way).
  const createStream = async (video: MediaStreamConstraints["video"], audio: MediaStreamConstraints["audio"]) => {
    const callAudio = audio ? localStream.value.getAudioTracks().filter(track => track.readyState === "live") : [];
    if (callAudio.length === 0) return getStream(video, audio);
    const camera = video ? (await getStream(video, false)).getVideoTracks() : [];
    return new MediaStream([...camera, ...callAudio]);
  };

  const produceLocalStream = async (
    connection: WebRTCConnection,
    { video, audio }: MediaStreamConstraints,
    shouldCreateStream = true
  ) => {
    localStream.value.removeEventListener("addtrack", onLocalStreamChanged);
    localStream.value.removeEventListener("removetrack", onLocalStreamChanged);
    // The camera state is applied twice. Before produceStream: a new track (the first one or after a camera switch)
    // goes to the call already off. After: while produceStream was awaited, the user could tap the button,
    // and toggleCamera applied it to the previous localStream.
    const media = shouldCreateStream ? applyCameraState(await createStream(video, audio)) : { video, audio };
    localStream.value = applyCameraState(await WebRTC.produceStream(connection, media));
    onLocalStreamChanged();
    localStream.value.addEventListener("addtrack", onLocalStreamChanged);
    localStream.value.addEventListener("removetrack", onLocalStreamChanged);
  };

  const consumeOpponentStream = async (
    connection: WebRTCConnection,
    { video, audio }: { video: boolean; audio: boolean },
    onData: (data: OpponentData) => void
  ) => {
    opponentStream.value.removeEventListener("addtrack", onOpponentStreamChanged);
    opponentStream.value.removeEventListener("removetrack", onOpponentStreamChanged);
    //this.opponentStream is not reactivity
    opponentStream.value = await WebRTC.consumeStream({
      connection,
      tracks: { video, audio },
      onData,
    });
    onOpponentStreamChanged();
    opponentStream.value.addEventListener("addtrack", onOpponentStreamChanged);
    opponentStream.value.addEventListener("removetrack", onOpponentStreamChanged);
  };

  const setVideoState = async (status: boolean) => {
    if (!callConnection.value) return;

    loading.value = true;
    // A video chat starts with both cameras off: everyone turns their own camera on with the button (CO-15610).
    // Out of the video chat there is no camera, the state is "on" again, as after the call
    cameraOff.value = status;
    opponentCameraOff.value = status;
    if (!status) {
      opponentReadyVideoState.value = status;
    }
    localReadyVideoState.value = status;
    WebRTC.webRTCData?.producer.client?.sendData({ video: status });

    try {
      businessLogicLog("Set video", { video: status, audio: true });
      await produceLocalStream(callConnection.value, { video: status, audio: true });
    } catch (error) {
      collectErrorLogs("Set video state", error as Error, { video: status, audio: true });
      WebRTC.webRTCData?.producer.client?.sendData({ videoState: false });
      // eslint-disable-next-line no-console
      console.warn(error);
    } finally {
      loading.value = false;
    }
  };

  // opponent data channel and producers-updated: the video chat request or end, and the camera state in it
  const changeOpponentVideoState = async (data: OpponentData) => {
    if ("cameraOff" in data) {
      opponentCameraOff.value = data.cameraOff;
      return;
    }
    const { video } = data;
    opponentReadyVideoState.value = video;
    try {
      if (callConnection.value) {
        await consumeOpponentStream(
          callConnection.value,
          { video: opponentReadyVideoState.value, audio: true },
          changeOpponentVideoState
        );
      }
      if (!video && localReadyVideoState.value) {
        await setVideoState(false);
      }
    } catch (error) {
      collectErrorLogs("Change opponent video state", error as Error);
      throw error;
    }
  };

  // Only in the video chat: the own camera goes off and on, the opponent's video stays on the screen
  const toggleCamera = async () => {
    cameraOff.value = !cameraOff.value;
    businessLogicLog("Toggle camera", { video: !cameraOff.value });
    applyCameraState(localStream.value);
    // both cameras are off: nothing to show, the call goes back to voice only
    if (cameraOff.value && opponentCameraOff.value) return setVideoState(false);
    await WebRTC.webRTCData?.producer.client?.sendData({ cameraOff: cameraOff.value });
  };

  const callCreate = async (data: { receiverId: string; subject: Subject<CallType> }) => {
    setDoNotDisturb(true);
    try {
      providers.CallsProvider.setReceiveErrors(false);
      businessLogicLog("Call created");
      return await providers.CallsProvider.create({ data });
    } catch (error) {
      collectErrorLogs("Call created", error as Error);
      setDoNotDisturb(false);
      throw error;
    }
  };

  const callAccept = async (callId: string) => {
    setDoNotDisturb(true);
    try {
      loading.value = true;
      providers.CallsProvider.setReceiveErrors(false);
      isAccepted.value = true;
      businessLogicLog("Call acceptation");
      return await providers.CallsProvider.accept({ callId });
    } catch (error) {
      collectErrorLogs("Call acceptation", error as Error);
      setDoNotDisturb(true);
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const preventLogout: PreventLogout = {
    value: true,
    type: PreventLogoutType.CALL_CONTROLLER,
    cb: async () => {
      await reset(true);
    },
  };

  // permission: the microphone request already started in the tap (CallController), otherwise it is made here
  const callOutgoingHandler = async (
    data: { subject?: Appointment; opponentUser: UserPublicData },
    permission?: Promise<void>
  ) => {
    opponentUser.value = data.opponentUser;
    if (!permission) {
      // a request started in the tap has already set its own message if it failed
      connectionErrorMessage.value = "";
      connectionErrorTitle.value = "";
    }
    try {
      loading.value = true;
      await (permission ?? getDevicePermission({ audio: true }));
      const { call: newCall, connection } = await callCreate({
        receiverId: data.opponentUser.id,
        subject: {
          subjectId: !!data.subject ? data.subject.id : "0",
          subjectType: !!data.subject ? CallType.APPOINTMENT : CallType.CALL,
        },
      });
      call.value = newCall;
      callConnection.value = connection;
      const context = { call: call.value, connection: callConnection.value };
      businessLogicLog("Setup outgoing call");
      CallsLogger.addBreadcrumb("Setup outgoing call", context);
      emitter.emit(EVENTS.PREVENT_LOGOUT, preventLogout);

      await produceLocalStream(callConnection.value, { video: false, audio: true });
    } catch (error) {
      collectErrorLogs("callOutgoingHandler", error as Error);
      await connectionErrorCb();
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const callUpdateHandler = async (newCall: Call) => {
    if (call.value?.id !== newCall.id || showConnectionError.value) return;
    call.value = newCall;
    if (call.value.status === CallStatus.ACCEPTED && callConnection.value) {
      if (!isAccepted.value && call.value.receiverId === authUserStrict.value.id) {
        businessLogicLog("Update call. Hide Notification. Call was accepted by other place");
        reset(false, true);
        return;
      }
      try {
        loading.value = true;
        businessLogicLog("Update call");
        await consumeOpponentStream(callConnection.value, { video: false, audio: true }, changeOpponentVideoState);
      } catch (error) {
        collectErrorLogs("Call updating", error as Error);
        throw error;
      } finally {
        loading.value = false;
      }
    } else {
      const ignore = call.value.status === CallStatus.DECLINED && call.value.receiverId === authUserStrict.value.id;
      reset(false, ignore);
    }
  };

  const callIncomingHandler = async (callCreateResponse: CallCreateResponse) => {
    if (!call.value && !doNotDisturb.value) {
      showNotification.value = true;
      opponentUser.value = callCreateResponse.call.caller;
      call.value = callCreateResponse.call;
      callConnection.value = callCreateResponse.connection;
      const context = { call: call.value, connection: callConnection.value };
      businessLogicLog("Setup incoming call");
      CallsLogger.addBreadcrumb("Setup incoming call", context);

      const subscriberResult = await providers.SubscriptionProvider.search({
        data: { consumerIds: [callCreateResponse.call.callerId], providerId: callCreateResponse.call.receiverId },
      });
      if (subscriberResult.result.length > 0) {
        opponentSubscriptionStatus.value = "subscriber";
      } else {
        const followerResult = await providers.FollowingsProvider.find({
          data: {
            userId: callCreateResponse.call.receiverId,
            followerId: callCreateResponse.call.callerId,
          },
        });
        if (followerResult.result.length > 0) {
          opponentSubscriptionStatus.value = "follower";
        }
      }
    }
  };

  const handleCallAccept = async () => {
    showNotification.value = false;
    if (call.value && callConnection.value) {
      try {
        loading.value = true;
        connectionErrorMessage.value = "";
        connectionErrorTitle.value = "";
        await getDevicePermission({ audio: true });
        call.value = await callAccept(call.value.id);
        await produceLocalStream(callConnection.value, { video: false, audio: true });
        await consumeOpponentStream(callConnection.value, { video: false, audio: true }, changeOpponentVideoState);
        emitter.emit(EVENTS.PREVENT_LOGOUT, preventLogout);
      } catch (error) {
        collectErrorLogs("Call accepting", error as Error);
        await connectionErrorCb();
        throw error;
      } finally {
        loading.value = false;
      }
    }
  };

  const handleCallBack = () => {
    businessLogicLog("Call back");
    const user = opponentUser.value;
    reset(true, true);
    emitter.emit(EVENTS.CALL_OUTGOING, { opponentUser: user });
  };

  const onChangeDevice = async (trackData: MediaTrackConstraints) => {
    businessLogicLog("Change device");
    if (!callConnection.value) return;
    if (isMobile()) {
      localStream.value.getVideoTracks().forEach(t => {
        t.stop();
        localStream.value.removeTrack(t);
      });
    }

    try {
      loading.value = true;
      await produceLocalStream(callConnection.value, { video: trackData, audio: true }, false);
    } catch (error) {
      collectErrorLogs("Change device", error as Error);
    } finally {
      loading.value = false;
    }
  };

  const init = () => {
    WebRTC = new StreamingHelper({
      consumerOnAllStreamClosed: () => {
        collectErrorLogs("Consumer all stream closed", new Error("All stream closed"));
        // the connection was lost: an earlier camera or microphone hint does not apply to it
        connectionErrorMessage.value = "";
        connectionErrorTitle.value = "";
        connectionErrorCb();
      },
    });
    WebRTC.on("close", e => {
      const error = (e.data as Error | undefined) ?? new Error("Client closed");
      collectErrorLogs("WebRTC Event close", error, { type: e.type });
    });
    WebRTC.on("producers-updated", changeOpponentVideoState);
    WebRTC.on("connectionstatechange", (state: connectionStateEnum) => {
      if (call.value?.status === CallStatus.NEW) return;
      connectingState.value = state;
    });
    WebRTC.logger.on("logging", ({ message, ...others }: LoggerContextWithMessage) => {
      CallsLogger.collectLogs(`[STREAM CLIENT] ${message}`, { streaming: true, ...others });
    });

    window.addEventListener("beforeunload", () => {
      if (
        call.value &&
        (call.value.status === CallStatus.ACCEPTED || call.value.callerId === authUserStrict.value.id)
      ) {
        businessLogicLog("window was closed");
        reset();
      }
      return "";
    });
  };

  const toggleBeeping = (call: Call | null) => {
    if (call?.status === CallStatus.NEW && call?.callerId === authUserStrict.value.id) {
      try {
        AudioPlayer.play("beep", { loop: true });
      } catch (error) {
        collectErrorLogs("Watch call", error as Error);
        // eslint-disable-next-line no-console
        console.warn(error);
      }
    } else {
      AudioPlayer.stop("beep");
    }
  };

  const checkStoppingGlobalPlayer = (call: Call | null, old: Call | null) => {
    if (call && !old) {
      pauseGlobalPlayer();
    }
  };

  watch(call, (call, old) => {
    checkStoppingGlobalPlayer(call, old);
    toggleBeeping(call);
  });

  return {
    opponentUser,
    showNotification,
    call,
    loading,
    opponentStream,
    localStream,
    localReadyVideoState,
    opponentReadyVideoState,
    showConnectionError,
    connectionErrorMessage,
    connectionErrorTitle,
    getDevicePermission,
    localVideoState,
    opponentVideoState,
    connectingState,
    opponentSubscriptionStatus,
    cameraOff,
    opponentCameraOff,
    setVideoState,
    toggleCamera,
    reset,
    handleCallBack,
    callCreate,
    callOutgoingHandler,
    callIncomingHandler,
    onChangeDevice,
    handleCallAccept,
    callUpdateHandler,
    collectErrorLogs,
    init,
  };
}
